import {useState} from 'react';
import {keys, NAVIGATION_KEYS} from '../../../../constants/KeyCodes';
import {isRTL} from '../../../../Localization';
import {KeyboardEvent as SyntheticKeyboardEvent} from 'react';

export interface KeyboardNavigationConfig {
    initialFocusedIndex: number;
    listLength: number;
    pageSize: number;
    circleNavigation?: boolean;
    onSpace?: (e: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>) => void;
    onMoveToEnd?: (e: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>) => void;
    onMoveToStart?: (e: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>) => void;
    onSelect?: (e: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>) => void;
    onNavigateLeft?: (e: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>) => void;
    onNavigateRight?: (e: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>) => void;
}
export const useKeyboardNavigation = (config: KeyboardNavigationConfig) => {
    const {
        initialFocusedIndex,
        listLength,
        pageSize,
        circleNavigation = true,
        onMoveToStart,
        onMoveToEnd,
        onSpace,
        onSelect,
        onNavigateLeft,
        onNavigateRight
    } = config;
    const [focusedIndex, setFocusedIndex] = useState(initialFocusedIndex);

    const preventDefault = (event: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>) => {
        let needsPrevent;
        switch (event.key) {
            case keys.ARROW_RIGHT:
                if (isRTL()) {
                    needsPrevent = !!onNavigateLeft;
                } else {
                    needsPrevent = !!onNavigateRight;
                }
                break;
            case keys.ARROW_LEFT:
                if (isRTL()) {
                    needsPrevent = !!onNavigateRight;
                } else {
                    needsPrevent = !!onNavigateLeft;
                }
                break;
            default:
                needsPrevent = true;
        }
        if (needsPrevent) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const handleKeyboardNavigation = (
        event: KeyboardEvent | SyntheticKeyboardEvent<HTMLElement>
    ) => {
        if ((event.target as HTMLButtonElement).name === 'msgbtn') return;

        if (
            NAVIGATION_KEYS.includes(event.key) ||
            (onSelect && event.key === keys.ENTER) ||
            (onSpace && event.key === keys.SPACE)
        ) {
            preventDefault(event);

            let nextFocusedIndex = focusedIndex;
            switch (event.key) {
                case keys.ARROW_UP:
                    if (focusedIndex === 0 || focusedIndex === -1) {
                        if (circleNavigation) nextFocusedIndex = listLength;
                        else {
                            onMoveToStart?.(event);
                            return;
                        }
                    } else {
                        nextFocusedIndex = focusedIndex - 1;
                    }
                    break;
                case keys.ARROW_DOWN:
                    if (focusedIndex === listLength) {
                        if (circleNavigation) nextFocusedIndex = 0;
                        onMoveToEnd?.(event);
                    } else {
                        nextFocusedIndex = focusedIndex + 1;
                    }
                    break;
                case keys.ARROW_RIGHT:
                    if (isRTL()) {
                        onNavigateLeft && onNavigateLeft(event);
                    } else {
                        onNavigateRight && onNavigateRight(event);
                    }
                    break;
                case keys.ARROW_LEFT:
                    if (isRTL()) {
                        onNavigateRight && onNavigateRight(event);
                    } else {
                        onNavigateLeft && onNavigateLeft(event);
                    }
                    break;
                case keys.PAGE_UP:
                    if (focusedIndex - pageSize > 0) {
                        nextFocusedIndex = focusedIndex - pageSize;
                    } else {
                        nextFocusedIndex = 0;
                    }
                    break;
                case keys.PAGE_DOWN:
                    if (focusedIndex + pageSize >= listLength) {
                        nextFocusedIndex = listLength;
                    } else {
                        nextFocusedIndex = focusedIndex + pageSize;
                    }
                    break;
                case keys.HOME:
                    nextFocusedIndex = 0;
                    break;
                case keys.END:
                    nextFocusedIndex = listLength - 1;
                    break;
                case keys.ENTER:
                    onSelect && onSelect(event);
                    break;
                case keys.SPACE:
                    onSpace && onSpace(event);

                    break;
            }

            setFocusedIndex(nextFocusedIndex);
        }
    };

    return {focusedIndex, handleKeyboardNavigation, setFocusedIndex};
};
